import React, { useMemo, useRef, useCallback, useState, useEffect } from 'react';
import { Box, Typography, Row, Col, Form, Loader, Iconography, Tooltip } from '@lucid/lego';
import type { FormRef } from '@lucid/lego';
import type { OrderDetailsResponse } from '../../../../../api/services/lupp/order/dto.order';
import { usePatchOrderDetails } from '../../../../../api/services/lupp/order/order';
import { useStore } from '../../../../../store/store';
import { formatDate } from '../../../../../utils/tools';
import { createRegistrationFormFields } from './formFields';
import { SUCCESS_MESSAGE_DURATION_MS, isPdiStatusReady } from './constants';
import { FieldDisplay } from './FieldDisplay/FieldDisplay';
import { StatusMessage } from './StatusMessage/StatusMessage';
import styles from './Registration.module.scss';

type RegistrationProps = {
  orderData: OrderDetailsResponse;
  theme: 'light' | 'dark';
  labels: Record<string, string>;
  dataTestId?: string;
};

/**
 * Form data structure for vehicle registration and delivery details
 *
 * @typedef {Object} RegistrationFormDefaultValues
 * @property {string} [firstRegistrationDate] - Date of the vehicle's first registration (ISO 8601 format)
 * @property {string} [licensePlateNumber] - License plate number assigned to the vehicle
 * @property {string} [registrationStatus] - Current status of the vehicle registration process
 * @property {string} [scheduledDeliveryDate] - Scheduled date for vehicle delivery (ISO 8601 format)
 * @property {string} [scheduledDeliveryTime] - Scheduled time for vehicle delivery (12-hour format, e.g., "2PM")
 */
export type RegistrationFormDefaultValues = {
  firstRegistrationDate?: string;
  licensePlateNumber?: string;
  registrationStatus?: string;
  scheduledDeliveryDate?: string;
  scheduledDeliveryTime?: string;
};

/**
 * Registration Details component
 * Displays vehicle readiness information and registration/delivery forms
 *
 * @component
 * @param {RegistrationProps} props - Component props
 * @returns {JSX.Element} Registration details with forms
 * @example
 * <Registration orderData={orderData} theme="light" labels={labels} />
 */
const Registration: React.FC<RegistrationProps> = ({
  orderData,
  theme,
  labels,
  dataTestId = 'registration-details',
}) => {
  const locale = useStore((state) => state.locale);
  const language = useStore((state) => state.language);
  const formRef = useRef<FormRef>(null);
  const [isPending, setIsPending] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const successTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Patch order details mutation
  const patchOrderDetailsMutation = usePatchOrderDetails();
  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
    };
  }, []);

  // Use retail delivery data from the main order response
  const registrationDeliveryDetails = orderData.retailDeliveryDetails;

  const {
    pdiStatus: rawPdiStatus,
    reapdiCompletedDate,
    vehicleReadyToSchedule,
  } = registrationDeliveryDetails?.vehicleReadiness || orderData.vehicleReadinessDetails || {};

  // When vehicleReadinessDetails is absent from the BE response, derive pdiStatus from orderStatus
  const pdiStatus =
    rawPdiStatus ||
    (orderData.orderStatus?.toLowerCase() === 'pdi completed' ? 'Completed' : undefined);

  const formFields = useMemo(() => {
    const defaultValues = {
      firstRegistrationDate:
        registrationDeliveryDetails?.firstRegistrationDetails?.firstRegistrationDate ?? undefined,
      licensePlateNumber:
        registrationDeliveryDetails?.firstRegistrationDetails?.licensePlateNumber ?? undefined,
      registrationStatus:
        registrationDeliveryDetails?.firstRegistrationDetails?.registrationStatus || '',
      scheduledDeliveryDate:
        registrationDeliveryDetails?.scheduledDeliveryDetails?.scheduledDeliveryDate ?? undefined,
      scheduledDeliveryTime:
        registrationDeliveryDetails?.scheduledDeliveryDetails?.scheduledDeliveryTime ?? undefined,
    };

    const vehicleReadyDate =
      vehicleReadyToSchedule || orderData.vehicleReadyToScheduleDate || undefined;

    return createRegistrationFormFields(labels, defaultValues, vehicleReadyDate);
  }, [
    labels,
    registrationDeliveryDetails,
    vehicleReadyToSchedule,
    orderData.vehicleReadyToScheduleDate,
  ]);
  const handleSuccess = useCallback(
    (data: unknown) => {
      const submitForm = async () => {
        setErrorMessage(null);
        setSuccessMessage(null);
        // Clear any existing timeout
        if (successTimeoutRef.current) {
          clearTimeout(successTimeoutRef.current);
          successTimeoutRef.current = null;
        }
        setIsPending(true);

        try {
          // Type assertion - trust that the form library provides correctly typed data
          const formData = data as unknown as RegistrationFormDefaultValues;

          // Prepare PATCH request payload using spread - apiKey names match DTO field names
          const patchData = {
            orderIdentifier: orderData.orderIdentifier,
            registrationDetailsPreDelivery: {
              firstRegistrationDetails: {
                firstRegistrationDate: formData.firstRegistrationDate,
                licensePlateNumber: formData.licensePlateNumber,
                registrationStatus: formData.registrationStatus,
              },
              scheduledDeliveryDates: {
                scheduledDeliveryDate: formData.scheduledDeliveryDate,
                scheduledDeliveryTime: formData.scheduledDeliveryTime || undefined,
              },
            },
          };

          await patchOrderDetailsMutation.mutateAsync(patchData);

          setSuccessMessage(
            labels?.['success.save'] ?? 'Registration details updated successfully'
          );

          // Clear previous timeout if exists
          if (successTimeoutRef.current) {
            clearTimeout(successTimeoutRef.current);
          }

          // Auto-hide success message
          successTimeoutRef.current = setTimeout(() => {
            setSuccessMessage(null);
          }, SUCCESS_MESSAGE_DURATION_MS);
        } catch (error: unknown) {
          // Extract error message from API response
          let errorMsg =
            labels?.['error.save'] ?? 'An error occurred while saving registration details';

          if (error && typeof error === 'object') {
            const apiError = error as {
              message?: string;
              response?: {
                data?: {
                  message?: string;
                  errors?: string[];
                };
              };
            };

            // Priority order: response.data.message > response.data.errors[0] > error.message
            if (apiError.response?.data?.message) {
              errorMsg = apiError.response.data.message;
            } else if (
              apiError.response?.data?.errors &&
              apiError.response.data.errors.length > 0
            ) {
              errorMsg = apiError.response.data.errors.join(', ');
            } else if (apiError.message && apiError.message !== 'Network Error') {
              errorMsg = apiError.message;
            }
          }

          setErrorMessage(errorMsg);

          // Clear previous timeout if exists
          if (errorTimeoutRef.current) {
            clearTimeout(errorTimeoutRef.current);
          }

          // Auto-hide error message
          errorTimeoutRef.current = setTimeout(() => {
            setErrorMessage(null);
          }, SUCCESS_MESSAGE_DURATION_MS);
        } finally {
          setIsPending(false);
        }
      };

      // Execute async function with error handling
      submitForm().catch(() => {
        setErrorMessage(labels?.['error.save'] ?? 'An error occurred');
        setIsPending(false);
      });
    },
    [orderData.orderIdentifier, patchOrderDetailsMutation, labels]
  );

  const handleError = useCallback(() => {
    setErrorMessage(labels?.['error.validation'] ?? 'Please correct the errors in the form');
    setSuccessMessage(null);

    // Clear previous timeout if exists
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
    }

    // Auto-hide validation error message
    errorTimeoutRef.current = setTimeout(() => {
      setErrorMessage(null);
    }, SUCCESS_MESSAGE_DURATION_MS);
  }, [labels]);

  return (
    <Box data-testid={dataTestId}>
      {/* Vehicle Readiness Section */}
      <Box mb={12}>
        <Typography variant="body" size="large" theme={theme} color="black">
          {labels?.['vehicle.readiness'] ?? 'Vehicle Readiness'}
        </Typography>
      </Box>
      <Box className={styles.readinessContainer} mb={24}>
        <Row gutter={[24, 0]}>
          <Col xs={12} sm={2} lg={2}>
            <FieldDisplay
              label={labels?.['pdi.status'] ?? 'PDI Status'}
              value={pdiStatus || labels?.['not.started'] || 'Not started'}
              theme={theme}
            />
          </Col>

          <Col xs={12} sm={3} lg={3}>
            <FieldDisplay
              label={labels?.['pdi.completedDate'] ?? 'PDI completed Date'}
              value={
                isPdiStatusReady(pdiStatus)
                  ? formatDate(reapdiCompletedDate, locale, {}, language ?? undefined)
                  : '-'
              }
              theme={theme}
            />
          </Col>

          <Col xs={12} sm={4} lg={4}>
            <Row gutter={[8, 0]}>
              <Col lg={12}>
                <Box
                  className={styles.labelWithTooltip}
                  data-testid="vehicle-ready-tooltip-trigger"
                >
                  <Typography variant="label" size="small" theme={theme} color="gray-7">
                    {labels?.['vehicleReadyToSchedule.date'] ?? 'Vehicle ready to Schedule Date'}
                  </Typography>
                  <Tooltip
                    content={
                      labels?.['vehicleReadyToSchedule.tooltip'] ??
                      'If populated, the vehicle has no open Work Orders or Service Campaigns and should be delivery-ready (excluding Detailing). Auto-populated and owned by Service, required for Final Quality Check.'
                    }
                    side="bottom"
                    theme={theme === 'dark' ? 'light' : 'dark'}
                    dataTestId={`${dataTestId}-vehicle-ready-tooltip`}
                  >
                    <span
                      className={styles.tooltipIcon}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                      }}
                      role="img"
                      aria-label={labels?.['tooltip.moreInformation'] ?? 'More information'}
                    >
                      <Iconography
                        category="global"
                        theme={theme}
                        name="information"
                        color="gray-5"
                      />
                    </span>
                  </Tooltip>
                </Box>
              </Col>
              <Col lg={12}>
                <Typography variant="body" size="extraSmall" theme={theme} color="black">
                  {isPdiStatusReady(pdiStatus)
                    ? formatDate(
                        vehicleReadyToSchedule ?? orderData.vehicleReadyToScheduleDate,
                        locale,
                        {},
                        language ?? undefined
                      )
                    : '-'}
                </Typography>
              </Col>
            </Row>
          </Col>
        </Row>
      </Box>

      {/* Messages */}
      {successMessage && <StatusMessage message={successMessage} type="success" theme={theme} />}
      {errorMessage && <StatusMessage message={errorMessage} type="error" theme={theme} />}

      {/* Loading Overlay */}
      {isPending && (
        <Box className={styles.fetchingOverlay}>
          <Loader size={32} theme={theme} />
        </Box>
      )}

      {/* Registration and Delivery Form */}
      <Box className={styles.formContainer}>
        <Form
          key={`registration-form-${orderData.orderIdentifier}-${JSON.stringify(registrationDeliveryDetails)}`}
          id="registration-details-form"
          ref={formRef}
          theme={theme === 'dark' ? 'light' : 'dark'}
          formFields={formFields}
          onSuccess={handleSuccess}
          onError={handleError}
          isFluid={true}
        />
      </Box>
    </Box>
  );
};

Registration.displayName = 'Registration';

export default Registration;
